import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import { RootState } from 'store'

import { ITranslationsState } from 'types/slices'
import {
  ITranslations,
  TRANSLATION_STATUS,
  TranslationProgress
} from 'types/translations'
import { LoadingStatus, TLoadingStatus } from 'types/user'
import { handleApiErrors } from 'utils/errors'

import TranslationRepository from './repositories/TranslationRepository'

const initialState: ITranslationsState = {
  status: 'initial',
  translations: {
    faq: '',
    technicalDrawing: '',
    packageContents: '',
    hotpointCursor: '',
    hotpointCursonNoSpace: '',
    moveCursor: '',
    discover: '',
    discoverFeatures: '',
    open: '',
    close: '',
    side: '',
    front: '',
    dimensions: '',
    summary: '',
    comparison: '',
    reccommendedTogether: '',
    check: '',
    comparisonSwipe: '',
    accessories: '',
    accessoriesIncluded: '',
    atYourHome: '',
    moveToUnfoldDescription: '',
    AIGenerated: '',
    AIModified: ''
  },
  translationProgresses: []
}

export const types = {
  getTranslations: 'translations/getTranslations',
  updateTranslationStatus: 'translations/updateTranslationStatus',
  clearTranslationJob: 'translations/clearTranslationJob',
  fetchTranslationProgress: 'translations/fetchTranslationProgress',
  cleanupTranslationProgress: 'translations/cleanupTranslationProgress'
} as const

export const getTranslations = createAsyncThunk(
  types.getTranslations,
  async (
    { brandId, country }: { brandId: string; country: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await TranslationRepository.getTranslations({
        brandId,
        country
      })

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)

export const updateTranslationStatus = createAsyncThunk(
  types.updateTranslationStatus,
  async (payload: TranslationProgress) => {
    return payload
  }
)

export const clearTranslationJob = createAsyncThunk(
  types.clearTranslationJob,
  async (translationId?: string) => {
    return translationId || null
  }
)

export const cleanupTranslationProgress = createAsyncThunk(
  types.cleanupTranslationProgress,
  async (translationId: string, { rejectWithValue }) => {
    try {
      await TranslationRepository.cleanupTranslationProgress(translationId)

      return translationId
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

export const fetchTranslationProgress = createAsyncThunk(
  types.fetchTranslationProgress,
  async (translationId: string, { rejectWithValue, getState }) => {
    try {
      const response = await TranslationRepository.getTranslationProgress(
        translationId
      )

      if (response.data.data) {
        const progressData = response.data.data

        // When status is 'completed', the productId field contains the translatedProductId
        if (progressData.status === 'completed') {
          // Extract translatedProductId from productId field when completed
          if (!progressData.translatedProductId && progressData.productId) {
            progressData.translatedProductId = progressData.productId
          }
        }

        return progressData
      } else {
        return rejectWithValue(
          response.data.message || 'Failed to fetch progress'
        )
      }
    } catch (err: any) {
      const errorMessage = err.response?.data || err.message

      if (
        typeof errorMessage === 'string' &&
        errorMessage.toLowerCase().includes('translation progress not found')
      ) {
        const state = getState() as any
        const currentProgress = state.translations.translationProgresses.find(
          (p: TranslationProgress) => p.id === translationId
        )

        if (
          currentProgress &&
          currentProgress.progress > 0 &&
          currentProgress.progress < 100
        ) {
          return {
            ...currentProgress,
            status: TRANSLATION_STATUS.completed,
            progress: 100,
            message: 'Translation completed successfully!',
            endTime: new Date().toISOString(),
            translatedProductId:
              currentProgress.translatedProductId || currentProgress.productId
          }
        }
      }

      return rejectWithValue(errorMessage)
    }
  }
)

const ensureTranslationProgressesArray = (state: ITranslationsState) => {
  if (!state.translationProgresses) {
    state.translationProgresses = []
  }
}


const findProgressIndex = (state: ITranslationsState, id: string) => {
  return state.translationProgresses.findIndex(p => p.id === id)
}

const removeProgressById = (state: ITranslationsState, id: string) => {
  state.translationProgresses = state.translationProgresses.filter(
    p => p.id !== id
  )
}

const updateOrAddProgress = (
  state: ITranslationsState,
  progress: TranslationProgress
) => {
  const existingIndex = findProgressIndex(state, progress.id)

  if (existingIndex >= 0) {
    state.translationProgresses[existingIndex] = progress
  } else {
    state.translationProgresses.push(progress)
  }
}

export const translationsSlice = createSlice({
  name: 'translations',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getTranslations.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(getTranslations.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.translations = action.payload
      })
      .addCase(getTranslations.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })
      .addCase(updateTranslationStatus.fulfilled, (state, action) => {
        const progress = action.payload

        ensureTranslationProgressesArray(state)
        updateOrAddProgress(state, progress)
      })
      .addCase(clearTranslationJob.fulfilled, (state, action) => {
        const translationId = action.payload

        ensureTranslationProgressesArray(state)

        if (translationId) {
          removeProgressById(state, translationId)
        } else {
          state.translationProgresses = []
        }
      })
      .addCase(fetchTranslationProgress.fulfilled, (state, action) => {
        const progress = action.payload

        ensureTranslationProgressesArray(state)
        updateOrAddProgress(state, progress)
      })
      .addCase(fetchTranslationProgress.rejected, (state, action) => {
        const translationId = action.meta.arg

        ensureTranslationProgressesArray(state)

        const translationIndex = findProgressIndex(state, translationId)

        if (translationIndex >= 0 && typeof action.payload === 'string') {
          if (
            !action.payload
              .toLowerCase()
              .includes('translation progress not found')
          ) {
            state.translationProgresses[translationIndex].status =
              TRANSLATION_STATUS.failed
            state.translationProgresses[translationIndex].error = action.payload
          }
        }
      })
  }
})

export const selectTranslations = (state: RootState) => state.translations
export const selectTranslationsStatus: (state: RootState) => TLoadingStatus =
  createSelector(
    selectTranslations,
    translationsSlice => translationsSlice.status
  )
export const selectTranslation: (state: RootState) => ITranslations =
  createSelector(selectTranslations, translations => translations.translations)

export const selectTranslationProgresses = (state: RootState) => {
  if (state.translations.translationProgresses !== undefined) {
    return state.translations.translationProgresses
  }

  if ((state.translations as any).translationProgress) {
    return [(state.translations as any).translationProgress]
  }

  return []
}

export const selectTranslationProgress = (
  state: RootState,
  translationId?: string
) => {
  const translationProgresses = selectTranslationProgresses(state)

  if (translationId) {
    return translationProgresses.find(p => p.id === translationId)
  }

  return translationProgresses[translationProgresses.length - 1]
}

export const selectTranslationProgressStatus = (
  state: RootState,
  translationId?: string
) => {
  const progress = selectTranslationProgress(state, translationId)

  return progress?.status
}

export default translationsSlice.reducer
